"use client";

import { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import type { ClientUserPermissions } from "@/lib/auth/client-permissions";
import {
  SIDEBAR_COLLAPSED_COOKIE,
  sidebarCollapsedCookieValue,
} from "@/lib/sidebar/cookies";

export interface ClientSidebarContentAvailability {
  campaigns: boolean;
  media: boolean;
  pulse: boolean;
  reports: boolean;
  sitemaps: boolean;
  social: boolean;
  /** clients.site_feedback_enabled: client users see Site Feedback only when on. */
  siteFeedback: boolean;
}

interface ClientSidebarData {
  clientId: string;
  clientName: string;
  providers: string[];
  customDashboards: { id: string; name: string }[];
  hiddenViews: string[];
  /** Canonical per-company grants used to keep links aligned with route guards. */
  permissions: ClientUserPermissions | null;
  contentAvailability: ClientSidebarContentAvailability;
  /** Per-company Social Publishing toggle (gates the client-role Social nav item). */
  socialEnabled?: boolean;
  /** Posts awaiting the signed-in user's approval (Social badge). */
  publishingPendingCount?: number;
  /** Per-company Sitemap Planner toggle (gates the client-role Sitemaps nav item). */
  sitemapsEnabled?: boolean;
}

interface ClientSidebarContextValue {
  client: ClientSidebarData | null;
  setClient: (data: ClientSidebarData | null) => void;
  collapsed: boolean;
  setCollapsed: (v: boolean) => void;
  /** Compact-screen navigation is transient UI state, never a persisted rail preference. */
  mobileNavOpen: boolean;
  setMobileNavOpen: (v: boolean) => void;
  demoMode: boolean;
  setDemoMode: (v: boolean) => void;
}

const ClientSidebarContext = createContext<ClientSidebarContextValue | null>(
  null
);

// Re-exported for existing client-side importers. The definition lives in a
// non-client module so the server can read it as a real string.
export { SIDEBAR_COLLAPSED_COOKIE };

function writeCollapsedCookie(v: boolean) {
  document.cookie = `${SIDEBAR_COLLAPSED_COOKIE}=${sidebarCollapsedCookieValue(v)}; path=/; max-age=31536000; SameSite=Lax`;
}

export function ClientSidebarProvider({
  children,
  initialCollapsed = false,
  initialDemoMode = false,
  clientModeActive = false,
}: {
  children: ReactNode;
  /** Read from the spark-sidebar-collapsed cookie by the root layout, so the
   *  server renders the same rail width the client will keep (no width flash). */
  initialCollapsed?: boolean;
  /** Read from the spark-demo cookie by the root layout. Without it the server
   *  always rendered demo mode as off, which mismatched on hydration. */
  initialDemoMode?: boolean;
  /** Client permission projection and fabricated demo data are mutually exclusive. */
  clientModeActive?: boolean;
}) {
  const [client, setClient] = useState<ClientSidebarData | null>(null);
  const [collapsed, setCollapsedState] = useState(initialCollapsed);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const [demoMode, setDemoModeState] = useState(() => {
    if (clientModeActive) return false;
    // The cookie is what the server rendered from; localStorage is the fallback
    // for sessions predating it (the mount effect below seeds the cookie).
    if (initialDemoMode) return true;
    if (typeof window === "undefined") return false;
    return localStorage.getItem("spark-demo-mode") === "true";
  });

  // Sync cookie on mount so server components see existing localStorage state
  useEffect(() => {
    if (clientModeActive) {
      localStorage.removeItem("spark-demo-mode");
      document.cookie = "spark-demo=; path=/; max-age=0; SameSite=Lax";
      return;
    }
    if (demoMode) {
      document.cookie = "spark-demo=1; path=/; max-age=31536000; SameSite=Lax";
    } else {
      document.cookie = "spark-demo=; path=/; max-age=0; SameSite=Lax";
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sessions predating the cookie kept this preference in localStorage only.
  // Seed the cookie from it so the server can render their width; it applies
  // from their next load rather than yanking the rail open under them now.
  useEffect(() => {
    if (document.cookie.includes(`${SIDEBAR_COLLAPSED_COOKIE}=`)) return;
    const stored = localStorage.getItem(SIDEBAR_COLLAPSED_COOKIE);
    writeCollapsedCookie(stored === null ? initialCollapsed : stored === "true");
  }, [initialCollapsed]);

  function setCollapsed(v: boolean) {
    setCollapsedState(v);
    localStorage.setItem(SIDEBAR_COLLAPSED_COOKIE, String(v));
    writeCollapsedCookie(v);
  }

  function setDemoMode(v: boolean) {
    if (clientModeActive) return;
    setDemoModeState(v);
    localStorage.setItem("spark-demo-mode", String(v));
    // Sync cookie so server components can read demo state
    if (v) {
      document.cookie = "spark-demo=1; path=/; max-age=31536000; SameSite=Lax";
    } else {
      document.cookie = "spark-demo=; path=/; max-age=0; SameSite=Lax";
    }
  }

  return (
    <ClientSidebarContext.Provider
      value={{
        client,
        setClient,
        collapsed,
        setCollapsed,
        mobileNavOpen,
        setMobileNavOpen,
        demoMode,
        setDemoMode,
      }}
    >
      {children}
    </ClientSidebarContext.Provider>
  );
}

export function useClientSidebar(): ClientSidebarContextValue {
  const ctx = useContext(ClientSidebarContext);
  if (!ctx) {
    throw new Error(
      "useClientSidebar must be used within a ClientSidebarProvider"
    );
  }
  return ctx;
}
